import React, { useCallback, useContext, useImperativeHandle, useState } from "react";
import { useI18n } from "../hooks/i18n-hooks";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { useMutation } from "@apollo/client";
import { CREATE_WORKER } from "../graphql/core";
import { SelectChooser, SelectItemType } from "./select/SelectChooser";
import { usePostOnboardingData } from "../hooks/use-postonboarding-data";
import { useApolloErrorSnackbar } from "../hooks/snackbar";
import {
    findProfileForUser,
    ProfileSwitcherManagerContext,
} from "../contexts/ProfileSwitcherManager";
import { ClientStatus } from "../__generated__/graphql";

const CREATE_NEW_TALENT_PROFILE = "create-talent-profile";
const CREATE_NEW_CLIENT_PROFILE = "create-client-profile";
const CREATE_NEW_PRIVATE_CLIENT_PROFILE = "create-private-client-profile";

const MAX_CLIENT_PROFILES = 10;

type ScreenParams = StackScreenProps<RootStackParamList>;

export type ProfileSwitcherRef = {
    open: () => void;
};

const ProfileSwitcher = () => {
    const i18n = useI18n();
    const { user, refreshUser } = useContext(AuthManagerContext);
    const { profileId, unreadProfilesActivities, setProfile, setRef } = useContext(
        ProfileSwitcherManagerContext,
    );
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const [isVisible, setIsVisible] = useState(false);

    const [
        initWorkerProfile,
        {
            loading: initWorkerProfileLoading,
            error: initWorkerProfileError,
            reset: initWorkerProfileReset,
        },
    ] = useMutation(CREATE_WORKER);

    useApolloErrorSnackbar(
        initWorkerProfileError,
        initWorkerProfileReset,
        i18n.t("profile.add_talent_profile_error"),
    );

    const { isValid: isWorkerProfileComplete } = usePostOnboardingData();

    const onSelect = useCallback(
        async ({ value }: { value: string }) => {
            if (!user) {
                return;
            }

            if (value === CREATE_NEW_TALENT_PROFILE) {
                const { data: initWorkerProfileData } = await initWorkerProfile({
                    variables: {
                        workerProfile: {},
                    },
                });
                navigation.navigate("PostOnboardingProfileEditor", {
                    newProfileId: `worker:${initWorkerProfileData?.createWorkerProfile.id}`,
                });
            } else if (value === CREATE_NEW_CLIENT_PROFILE) {
                navigation.navigate("CheckClientInfo", {
                    isNewProfile: true,
                });
            } else if (value === CREATE_NEW_PRIVATE_CLIENT_PROFILE) {
                navigation.navigate("CheckPrivateClientInfo", {
                    isNewProfile: true,
                });
            } else {
                // Switch to existing profile
                if (user.workerProfile?.id === value) {
                    if (isWorkerProfileComplete) {
                        await setProfile(user.workerProfile);
                    } else {
                        navigation.navigate("PostOnboardingProfileEditor", {
                            newProfileId: `worker:${user.workerProfile.id}`,
                        });
                    }
                } else {
                    const profile = findProfileForUser(value, user);
                    await setProfile(profile);
                }
            }
            // be sure we refresh user after profile ID change
            setTimeout(() => {
                void refreshUser?.();
            }, 1);
            setIsVisible(false);
        },
        [initWorkerProfile, isWorkerProfileComplete, navigation, refreshUser, setProfile, user],
    );

    const open = useCallback(() => {
        setIsVisible(true);
    }, []);

    useImperativeHandle(setRef, () => ({
        open,
    }));

    const onDismiss = useCallback(() => {
        setIsVisible(false);
    }, []);

    return user ? (
        <SelectChooser
            visible={isVisible}
            onDismiss={onDismiss}
            halfScreen
            lightMode
            selectedValue={profileId}
            onSelect={onSelect}
            disabled={initWorkerProfileLoading}
            isLegacyLabel={false}
            searchItems={
                [
                    ...(user.workerProfile
                        ? [
                              {
                                  value: user.workerProfile.id,
                                  unread:
                                      isWorkerProfileComplete &&
                                      unreadProfilesActivities[user.workerProfile.id],
                                  avatarPath: user.avatarUrl,
                                  canEnlargeAvatar: false,
                                  useBigAvatarOrIcon: true,
                                  title: i18n.t("profile.worker"),
                                  selectable: isWorkerProfileComplete,
                                  labels: [
                                      {
                                          text: !isWorkerProfileComplete
                                              ? i18n.t("profile.pending_creation")
                                              : undefined,
                                          mode: "default",
                                      },
                                  ],
                              },
                          ]
                        : []),
                    ...(user.clientProfiles || []).map((clientProfile) => {
                        let props = {};

                        switch (clientProfile.client.status) {
                            case ClientStatus.Accepted:
                                props = {
                                    unread: unreadProfilesActivities[clientProfile.id],
                                };
                                break;
                            case ClientStatus.Pending:
                                props = {
                                    labels: [
                                        {
                                            text: i18n.t("profile.pending_validation"),
                                            mode: "warning",
                                        },
                                    ],
                                };
                                break;
                            case ClientStatus.Rejected:
                                props = {
                                    labels: [
                                        {
                                            text: i18n.t("profile.rejected_validation"),
                                            mode: "error",
                                        },
                                    ],
                                };
                                break;
                        }
                        return {
                            value: clientProfile.id,
                            avatarPath: clientProfile.client.logoUrl || user?.avatarUrl,
                            canEnlargeAvatar: false,
                            useBigAvatarOrIcon: true,
                            ...(clientProfile.isPrivateClient
                                ? {
                                      title: i18n.t("profile.private_client"),
                                  }
                                : {
                                      title: i18n.t("profile.client"),
                                      description: clientProfile.client.name || undefined,
                                  }),
                            ...props,
                        };
                    }),
                    ...(!user.workerProfile
                        ? [
                              {
                                  value: CREATE_NEW_TALENT_PROFILE,
                                  labels: [
                                      { text: i18n.t("profile.add_talent_profile"), mode: "cta" },
                                  ],
                                  selectable: false,
                                  loading: initWorkerProfileLoading,
                              },
                          ]
                        : []),
                    ...(!(user.clientProfiles || []).some(
                        (clientProfile) => clientProfile.isPrivateClient,
                    )
                        ? [
                              {
                                  value: CREATE_NEW_PRIVATE_CLIENT_PROFILE,
                                  labels: [
                                      {
                                          text: i18n.t("profile.add_private_client_profile"),
                                          mode: "cta",
                                      },
                                  ],
                                  selectable: false,
                              },
                          ]
                        : []),
                    ...((user.clientProfiles || []).length < MAX_CLIENT_PROFILES
                        ? [
                              {
                                  value: CREATE_NEW_CLIENT_PROFILE,
                                  labels: [
                                      { text: i18n.t("profile.add_client_profile"), mode: "cta" },
                                  ],
                                  selectable: false,
                              },
                          ]
                        : []),
                ] as SelectItemType<string>[]
            }
        />
    ) : null;
};

export default ProfileSwitcher;
